import { useAuthStore } from '../stores/auth'

const API_BASE = '/api/v1'

class APIError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

let refreshPromise: Promise<boolean> | null = null

async function doRefresh(): Promise<boolean> {
	const refreshToken = useAuthStore.getState().refreshToken
  if (!refreshToken) return false

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })
    if (!res.ok) return false
    const data = await res.json()
    if (data.access_token && data.refresh_token) {
      useAuthStore.getState().setTokens(data.access_token, data.refresh_token)
      return true
    }
    return false
  } catch {
    return false
  }
}

async function refreshTokens(): Promise<boolean> {
  if (refreshPromise) return refreshPromise
  refreshPromise = doRefresh().finally(() => { refreshPromise = null })
  return refreshPromise
}

function forceLogout() {
  useAuthStore.getState().logout()
  const currentPath = window.location.pathname
  if (currentPath !== '/login') {
    window.location.href = `/login?reason=session_expired&return=${encodeURIComponent(currentPath)}`
  }
}

const AUTH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/mfa/totp/enroll', '/auth/accept-invite', '/auth/forgot', '/auth/reset']

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; timeout?: number; _retry?: boolean } = {}
): Promise<T> {
  const { method = 'GET', body, timeout = 10000, _retry = false } = options
  const url = path.startsWith('http') ? path : `${API_BASE}${path}`

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)

  const { accessToken } = useAuthStore.getState()
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })

    // Handle 401 — attempt token refresh (once)
    if (res.status === 401 && !_retry && !AUTH_PATHS.some((p) => path.includes(p))) {
      const refreshed = await refreshTokens()
      if (refreshed) {
        return request<T>(path, { ...options, _retry: true })
      }
      forceLogout()
      throw new APIError('Session expired', 401)
    }

    const text = await res.text()
    let data: unknown
    try {
      data = JSON.parse(text)
    } catch {
      data = text
    }

    if (!res.ok) {
      if (res.status === 429) {
        throw new APIError('Too many requests. Please wait a moment and try again.', 429)
      }
      const msg =
        data && typeof data === 'object' && 'error' in data
          ? (data as { error: string }).error
          : typeof data === 'string'
            ? data
            : `Request failed (${res.status})`
      throw new APIError(msg, res.status)
    }

    return data as T
  } catch (err) {
    if (err instanceof APIError) throw err
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new APIError('Request timed out', 408)
    }
    throw new APIError('Network error', 0)
  } finally {
    clearTimeout(timer)
  }
}

export const api = {
  get: <T>(path: string, options?: { timeout?: number }) =>
    request<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: { timeout?: number }) =>
    request<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: { timeout?: number }) =>
    request<T>(path, { ...options, method: 'PUT', body }),

  delete: <T>(path: string, options?: { timeout?: number }) =>
    request<T>(path, { ...options, method: 'DELETE' }),
}

export { forceLogout }
